/**
 * MZ-004 — MA000018 cl 29.2 public holiday choice: one eligibility and one
 * postability rule shared by every pricing path and the leave generator.
 *
 * cl 29.2(a)(ii) (full-time) and cl 29.2(b)(ii) (part-time) let a permanent
 * employee take the hours worked on a public holiday as annual leave instead
 * of the additional 150%. Casual employees have no choice (cl 29.2(c)).
 *
 * Rule (R3 review, 6 Oct 2026): the cash premium is NEVER withheld unless the
 * leave credit for that worker can actually be posted. Postable means the
 * tenant's leave writes are on, one Xero organisation is connected, Annual
 * Leave is mapped to a Xero leave type, the worker is matched to their Xero
 * employee, and the worker is eligible on the SAME employment type pricing
 * uses (the effective classification's employment_type). Otherwise the worker
 * is paid the cash 150% and payroll readiness says why.
 */

/** Behaviour version of these rules. Bump on any change. */
export const PUBLIC_HOLIDAY_ELECTION_RULES_VERSION = '1.0.0';

export const PUBLIC_HOLIDAY_ELECTION_CLAUSE_FULL_TIME = 'MA000018 cl 29.2(a)(ii)';
export const PUBLIC_HOLIDAY_ELECTION_CLAUSE_PART_TIME = 'MA000018 cl 29.2(b)(ii)';

/**
 * Eligible on the employment type pricing uses. CASUAL (and an unknown or
 * missing type) is never eligible; FULL_TIME, PART_TIME and PERMANENT are.
 */
export function publicHolidayElectionEligible(employmentType: string | null | undefined): boolean {
  const value = String(employmentType ?? '')
    .trim()
    .toUpperCase();
  return value === 'FULL_TIME' || value === 'PART_TIME' || value === 'PERMANENT';
}

export function publicHolidayElectionClause(employmentType: string | null | undefined): string {
  return String(employmentType ?? '').toUpperCase() === 'FULL_TIME'
    ? PUBLIC_HOLIDAY_ELECTION_CLAUSE_FULL_TIME
    : PUBLIC_HOLIDAY_ELECTION_CLAUSE_PART_TIME;
}

export type PublicHolidayLeaveCreditBlocker =
  | 'NOT_ELIGIBLE'
  | 'LEAVE_WRITES_OFF'
  | 'XERO_NOT_CONNECTED'
  | 'ANNUAL_LEAVE_UNMAPPED'
  | 'WORKER_NOT_MATCHED';

export interface PublicHolidayLeaveCreditFacts {
  employmentType: string | null | undefined;
  leaveWritesEnabled: boolean;
  /** Exactly one CONNECTED Xero organisation. */
  xeroConnected: boolean;
  annualLeaveMapped: boolean;
  workerMatched: boolean;
}

export type PublicHolidayLeaveCreditPostability =
  | { postable: true }
  | { postable: false; blocker: PublicHolidayLeaveCreditBlocker; reason: string };

const REASON: Record<PublicHolidayLeaveCreditBlocker, string> = {
  NOT_ELIGIBLE: 'only full-time and part-time workers can take public holiday hours as leave',
  LEAVE_WRITES_OFF: 'sending leave changes to Xero is turned off for your organisation',
  XERO_NOT_CONNECTED: 'no single Xero organisation is connected',
  ANNUAL_LEAVE_UNMAPPED: 'Annual Leave is not mapped to a Xero leave type',
  WORKER_NOT_MATCHED: 'this worker is not matched to a Xero employee',
};

/** The single postability decision. Checks run in a fixed order so the first missing step is named. */
export function publicHolidayLeaveCreditPostability(
  facts: PublicHolidayLeaveCreditFacts,
): PublicHolidayLeaveCreditPostability {
  const blocker: PublicHolidayLeaveCreditBlocker | null = !publicHolidayElectionEligible(
    facts.employmentType,
  )
    ? 'NOT_ELIGIBLE'
    : facts.leaveWritesEnabled !== true
      ? 'LEAVE_WRITES_OFF'
      : facts.xeroConnected !== true
        ? 'XERO_NOT_CONNECTED'
        : facts.annualLeaveMapped !== true
          ? 'ANNUAL_LEAVE_UNMAPPED'
          : facts.workerMatched !== true
            ? 'WORKER_NOT_MATCHED'
            : null;
  return blocker ? { postable: false, blocker, reason: REASON[blocker] } : { postable: true };
}
